import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container, TextField, Checkbox, Button, FormControlLabel, Grid,
  Card, CardMedia, CardActions, Dialog, DialogTitle, DialogContent,
  DialogActions, Typography, Select, MenuItem, InputLabel, Snackbar,
  Alert, Box, IconButton, Divider, Backdrop, CircularProgress,
  useMediaQuery, useTheme
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import QRCode from 'qrcode.react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import {
  API_BASE_URL, PRODUCTOALTA, PRODUCTOEDITAR, RUTAPRODUCTOSIMAGENES,
  PRODUCTOFOTOS, ELIMINARFOTO, RUBROSLISTAR, BASE_URL_QR
} from '../services/apiConfig';
import { MenuInicial } from './MenuInicial';
import { registrarAuditoria, isAuthenticated } from '../services/api.js';

const ProductoAMC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const userData = JSON.parse(sessionStorage.getItem("userData") || '{}');
  const usuarioid = userData?.usuarioid;
  const perfil = userData?.perfil;
  
  const [productData, setProductData] = useState({
    id: '',
    nombre: '',
    descripcion: '',
    activo: false,
    precio: '',
    precio_web: '',
    mostrar_web: true,
    unidades: '',
    fotos: [],
    fotoPredeterminada: '',
  });
  
  const [rubros, setRubros] = useState([]);
  const [selectedRubro, setSelectedRubro] = useState('');
  const [rubroError, setRubroError] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [localImages, setLocalImages] = useState([]);
  const [imagePreview, setImagePreview] = useState('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteIsLocal, setDeleteIsLocal] = useState(false);
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const autenticado = isAuthenticated();
    if (autenticado === false) {
      navigate("/login");
      return;
    }

    const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

    if (location.state?.productToEdit) {
      const productToEdit = location.state.productToEdit;
      // Convertir desde formato MySQL a display
      const fmtPrice = (p) => {
        if (!p) return '';
        const n = parseFloat(p);
        return isNaN(n) ? '' : n.toLocaleString('es-AR');
      };
      obtenerRutasDeImagenes(productToEdit.id).then((rutas) => {
        const fotoPredeterminada = rutas.length > 0 ? rutas[0] : '';
        setProductData({
          ...productToEdit,
          precio: fmtPrice(productToEdit.precio),
          precio_web: fmtPrice(productToEdit.precio_web),
          mostrar_web: productToEdit.mostrar_web === 1 || productToEdit.mostrar_web === '1' || productToEdit.mostrar_web === null,
          fotos: rutas,
          fotoPredeterminada: fotoPredeterminada,
          activo: productToEdit.activo === 1 || productToEdit.activo === '1',
        });
        setSelectedRubro(productToEdit.rubroid);
        setIsEditMode(true);
      });
    }

    fetchRubros();
  }, [navigate, location.state]);

  const fetchRubros = async () => {
    try {
      const response = await axios.get(API_BASE_URL + RUBROSLISTAR);
      setRubros(response.data);
    } catch (error) {
      console.error('Error al obtener rubros:', error);
    }
  };

  const obtenerRutasDeImagenes = async (productId) => {
    try {
      const response = await axios.get(API_BASE_URL + PRODUCTOFOTOS + '?id=' + productId);
      return response.data.rutas || [];
    } catch (error) {
      console.error('Error al obtener rutas de imágenes:', error);
      return [];
    }
  };

  const formatPriceDisplay = (value) => {
    if (!value) return '';
    const num = parseFloat(String(value).toFixed(0));
    if (isNaN(num)) return '';
    return num.toLocaleString('es-AR');
  };
  
  const formatPriceInput = (value) => {
    if (!value) return '';
    const num = String(value).replace(/[^\d]/g, '');
    return parseInt(num, 10).toString();
  };

  const parsePrice = (value) => {
    if (!value) return '0';
    return String(value).replace(/\./g, '');
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (type === 'checkbox') {
      setProductData({ ...productData, [name]: checked });
      return;
    }
    
    setProductData({ ...productData, [name]: value });
  };

  const handlePrecioBlur = (event) => {
    const name = event.target.name;
    if (productData[name]) {
      const num = productData[name].replace(/\./g, '');
      if (num) {
        setProductData({ ...productData, [name]: parseInt(num, 10).toLocaleString('es-AR') });
      }
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Solo tomar la primera imagen
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      setLocalImages([{
        file: file,
        preview: e.target.result,
        isLocal: true
      }]);
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleEliminarImagenExistente = (foto) => {
    setImageToDelete(foto);
    setDeleteIsLocal(false);
    setDeleteDialogOpen(true);
  };

  const handleEliminarImagenNueva = (index) => {
    const updatedImages = [...localImages];
    updatedImages.splice(index, 1);
    setLocalImages(updatedImages);
    
    if (updatedImages.length > 0) {
      setImagePreview(updatedImages[0].preview);
    } else {
      setImagePreview('');
    }
  };

  const eliminarImagen = async () => {
    try {
      if (!deleteIsLocal && imageToDelete) {
        console.log('Eliminando imagen:', { ruta: imageToDelete, productoid: productData.id });
        
        const response = await axios.post(API_BASE_URL + ELIMINARFOTO, { 
          ruta: imageToDelete, 
          productoid: productData.id 
        });
        
        console.log('Respuesta del servidor:', response.data);
        
        if (response.data.status === 'success' || response.data.success) {
          const nuevasFotos = productData.fotos.filter(foto => foto !== imageToDelete);
          setProductData({
            ...productData,
            fotos: nuevasFotos,
            fotoPredeterminada: nuevasFotos.length > 0 ? nuevasFotos[0] : ''
          });
          
          if (imagePreview.includes(imageToDelete)) {
            setImagePreview(nuevasFotos.length > 0 
              ? RUTAPRODUCTOSIMAGENES + nuevasFotos[0] 
              : ''
            );
          }
          
          setSnackbar({ 
            open: true, 
            message: 'Imagen eliminada correctamente', 
            severity: 'success' 
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: response.data.message || 'Error al eliminar imagen', 
            severity: 'error' 
          });
        }
      } else if (deleteIsLocal) {
        setLocalImages([]);
        setImagePreview('');
        setSnackbar({ 
          open: true, 
          message: 'Imagen local eliminada', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error al eliminar imagen: ' + (error.response?.data?.message || error.message), 
        severity: 'error' 
      });
    } finally {
      setDeleteDialogOpen(false);
      setImageToDelete(null);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!selectedRubro) {
      setRubroError(true);
      return;
    }
    setRubroError(false);

    const formData = new FormData();
    formData.append('nombre', productData.nombre);
    formData.append('descripcion', productData.descripcion);
    formData.append('activo', productData.activo ? 1 : 0);
    // Quitar puntos para guardar en MySQL (26.500 -> 26500)
    formData.append('precio', String(productData.precio).replace(/\./g, ''));
    formData.append('precio_web', productData.precio_web ? String(productData.precio_web).replace(/\./g, '') : '');
    formData.append('mostrar_web', productData.mostrar_web ? 1 : 0);
    formData.append('unidades', productData.unidades);
    formData.append('rubro', selectedRubro);

    if (productData.id) {
      formData.append('productoid', productData.id);
    }

    localImages.forEach((img, index) => {
      formData.append(`foto${index}`, img.file);
    });

setSaving(true);
     
    try {
      const url = productData.id 
        ? `${API_BASE_URL}${PRODUCTOEDITAR}`
        : `${API_BASE_URL}${PRODUCTOALTA}`;
      
      const TOKEN = sessionStorage.getItem("authToken");
      
      const response = await axios.post(url, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': TOKEN ? `Bearer ${TOKEN}` : ''
        }
      });

      if (response.data?.status === 'success' || response.data?.success) {
        if (productData.id) {
          registrarAuditoria(
            'Editar', 'Producto', productData.id + ' - ' + productData.nombre,
            'todos', '', '', 'ProductoAMC', usuarioid
          );
        }
        setSnackbar({ 
          open: true, 
          message: productData.id ? 'Producto actualizado' : 'Producto creado', 
          severity: 'success' 
        });
        setTimeout(() => navigate('/productoList'), 1500);
      } else {
        setSnackbar({ open: true, message: response.data?.message || 'Error', severity: 'error' });
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Error al guardar producto', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const qrValue = `${BASE_URL_QR}=${productData.id}`;

  const downloadQRCode = () => {
    const input = document.getElementById('qrCodeElement');
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      pdf.addImage(imgData, 'PNG', 10, 10);
      pdf.save(`QRCode_${productData.id}.pdf`);
    });
  };

  return (
    <Container maxWidth={isMobile ? false : "xl"} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      
      <Box sx={{ my: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Editar Producto' : 'Crear Producto'}
        </Typography>

        <form onSubmit={handleFormSubmit}>
          <Grid container spacing={3}>
            {isEditMode && (
              <Grid item xs={12} md={4}>
                <TextField
                  label="ID"
                  name="id"
                  value={productData.id}
                  disabled
                  fullWidth
                />
              </Grid>
            )}

            {isEditMode && Number(perfil) === 1 && (
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box id="qrCodeElement" sx={{ bgcolor: 'white', p: 1, display: 'inline-block' }}>
                    <QRCode value={qrValue} size={100} />
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={downloadQRCode}
                    sx={{ mt: 1, display: 'block', mx: 'auto' }}
                  >
                    Descargar QR
                  </Button>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={isEditMode && Number(perfil) === 1 ? 4 : 12}>
              <TextField
                label="Nombre"
                name="nombre"
                value={productData.nombre}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descripción"
                name="descripcion"
                value={productData.descripcion}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Precio"
                name="precio"
                type="text"
                value={productData.precio}
                onChange={handleInputChange}
                onBlur={handlePrecioBlur}
                placeholder="1.000,00"
                fullWidth
                required
                inputProps={{ 
                  inputMode: 'decimal',
                  pattern: '[0-9.,]*'
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Precio Web"
                name="precio_web"
                type="text"
                value={productData.precio_web || ''}
                onChange={handleInputChange}
                onBlur={handlePrecioBlur}
                placeholder="1.000,00 (opcional)"
                fullWidth
                helperText="Precio para ventas online. Si está vacío, usa el precio normal."
                inputProps={{ 
                  inputMode: 'decimal',
                  pattern: '[0-9.,]*'
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={productData.mostrar_web === true || productData.mostrar_web === 1}
                    onChange={(e) => setProductData({ ...productData, mostrar_web: e.target.checked })}
                    name="mostrar_web"
                  />
                }
                label="Mostrar en Web (Carrito)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Unidades (Stock)"
                name="unidades"
                type="number"
                value={productData.unidades}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={productData.activo}
                    onChange={handleInputChange}
                    name="activo"
                  />
                }
                label="Producto Activo"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel id="rubro-label">Rubro</InputLabel>
              <Select
                labelId="rubro-label"
                value={selectedRubro}
                onChange={(e) => setSelectedRubro(e.target.value)}
                fullWidth
                required
                error={rubroError}
              >
                {rubros.map((rubro) => (
                  <MenuItem key={rubro.id} value={rubro.id}>
                    {rubro.nombre}
                  </MenuItem>
                ))}
              </Select>
              {rubroError && <Typography color="error" variant="caption">Seleccione un rubro</Typography>}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Imágenes del Producto</Typography>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddPhotoAlternateIcon />}
                fullWidth
                sx={{ py: 2 }}
              >
                Agregar Nueva Imagen
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                />
              </Button>
            </Grid>

            {productData.fotos.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Imágenes Actuales ({productData.fotos.length})
                </Typography>
                <Grid container spacing={2}>
                  {productData.fotos.map((foto, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`existing-${index}`}>
                      <Card sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="150"
                          image={RUTAPRODUCTOSIMAGENES + foto}
                          alt={`Imagen ${index + 1}`}
                          sx={{ objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/150?text=Sin+Imagen';
                          }}
                        />
                        <CardActions sx={{ justifyContent: 'center', pb: 1 }}>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleEliminarImagenExistente(foto)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {localImages.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Nuevas Imágenes ({localImages.length})
                </Typography>
                <Grid container spacing={2}>
                  {localImages.map((img, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`local-${index}`}>
                      <Card sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="150"
                          image={img.preview}
                          alt={`Nueva imagen ${index + 1}`}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardActions sx={{ justifyContent: 'center', pb: 1 }}>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleEliminarImagenNueva(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {productData.fotos.length === 0 && localImages.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 4, 
                  bgcolor: 'grey.100', 
                  borderRadius: 2 
                }}>
                  <Typography color="text.secondary">
                    No hay imágenes. Haga clic en "Agregar Nueva Imagen" para añadir.
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/productoList')}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                >
                  {productData.id ? 'Actualizar Producto' : 'Crear Producto'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar esta imagen? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={eliminarImagen} color="error">Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={saving}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
            Por favor espere...
          </Typography>
        </Box>
      </Backdrop>
    </Container>
  );
};

export default ProductoAMC;
